// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { Mint } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Mint';
import {
  AllocationFactory,
  AllocationFactory_RequestMint,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import { ContractId } from '@daml/types';
import { toDamlDisclosedContract } from '../../../../utils/common';
import { ERR_REQ_MINT, SUC_REQ_MINT } from '../../../../utils/strings';
import { useCreateMintRequestContext } from './context/useCreateMintRequestContext';

interface Args {
  party: string;
  amount: string;
  source: string;
  instrumentId: string;
  reference?: string;
}

export const useRequestMint2Step = () => {
  const getCreateRequestMintContext = useCreateMintRequestContext();
  const { submit: createMintRequest } = useExerciseSubmission(
    AllocationFactory.AllocationFactory_RequestMint,
    SUC_REQ_MINT,
    ERR_REQ_MINT,
  );

  const now = new Date();
  return async (args: Args) => {
    const { source, party, amount, instrumentId, reference } = args;

    const createRequestMintContext = await getCreateRequestMintContext.mutateAsync({
      registrar: source,
      holder: party,
      instrumentId,
    });

    const mint: Mint = {
      instrumentId: {
        admin: source,
        id: instrumentId,
      },
      amount,
      holder: party,
      reference: reference || '',
      requestedAt: now.toISOString(),
      executeBefore: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days later
      meta: { values: {} },
    };

    const payload: AllocationFactory_RequestMint = {
      expectedAdmin: source,
      mint,
      extraArgs: {
        context: createRequestMintContext.choiceContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };

    const disclosedContracts =
      createRequestMintContext.choiceContext.disclosedContracts.map(toDamlDisclosedContract) || [];

    await createMintRequest({
      cid: createRequestMintContext.factoryId as ContractId<AllocationFactory>,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
};
